import { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, Info } from "lucide-react";

const SECTIONS = ["mentions", "cgv", "confidentialite"];

export default function Legal() {
  const { section } = useParams();
  const nav = useNavigate();
  const active = SECTIONS.includes(section) ? section : "mentions";

  useEffect(() => {
    if (!section) nav("/legal/mentions", { replace: true });
  }, [section, nav]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="legal-page">
      <div className="mb-6">
        <div className="font-mono-tech text-xs uppercase tracking-wider text-slate-500">
          Informations légales
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F2137] mt-1">
          Cadre juridique
        </h1>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 mb-6 flex items-start gap-3" data-testid="beta-notice">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <span className="font-semibold">Phase de test (Beta)</span> — ChantiPlanning est
          actuellement exploité par un particulier à des fins d'évaluation et
          d'expérimentation. Le service peut évoluer, être suspendu ou modifié à tout moment
          sans préavis. Aucun engagement de disponibilité ni SLA n'est garanti.
        </div>
      </div>

      <Tabs value={active} onValueChange={(v) => nav(`/legal/${v}`)} className="w-full">
        <TabsList data-testid="legal-tabs">
          <TabsTrigger value="mentions" data-testid="tab-mentions">Mentions légales</TabsTrigger>
          <TabsTrigger value="cgv" data-testid="tab-cgv">CGU / CGV</TabsTrigger>
          <TabsTrigger value="confidentialite" data-testid="tab-confidentialite">Confidentialité</TabsTrigger>
        </TabsList>

        {/* ---------- Mentions légales ---------- */}
        <TabsContent value="mentions" className="mt-6">
          <article className="prose max-w-none space-y-6" data-testid="content-mentions">
            <Section title="Éditeur du site">
              <p>
                Le site <strong>ChantiPlanning</strong> est édité par un particulier dans
                un cadre expérimental non-commercial. Pour toute question,
                contactez : <em>[à compléter — email de contact]</em>.
              </p>
              <p className="text-slate-500 italic text-xs">
                Le service n'étant pas encore exploité par une structure juridique
                déclarée, les informations d'éditeur (raison sociale, SIRET, TVA, adresse
                professionnelle) ne sont pas applicables. Elles seront ajoutées si
                l'activité venait à être formalisée.
              </p>
            </Section>

            <Section title="Hébergement">
              <p>
                Le site est hébergé sur la plateforme <strong>Emergent</strong> (cluster
                Kubernetes managé). Coordonnées de l'hébergeur communiquées sur demande.
              </p>
            </Section>

            <Section title="Propriété intellectuelle">
              <p>
                Le contenu, la marque et le code du site sont protégés. Toute
                reproduction, revente ou exploitation commerciale sans autorisation écrite
                préalable est interdite.
              </p>
            </Section>
          </article>
        </TabsContent>

        {/* ---------- CGU / CGV ---------- */}
        <TabsContent value="cgv" className="mt-6">
          <article className="prose max-w-none space-y-6" data-testid="content-cgv">
            <Section title="Article 1 — Nature du service">
              <p>
                ChantiPlanning est un service en ligne d'assistance à la génération de
                plannings de chantier destiné aux professionnels du BTP. Dans sa phase
                actuelle de test, il est proposé <strong>tel quel</strong>, sans garantie
                de résultat ni de continuité.
              </p>
            </Section>

            <Section title="Article 2 — Accès et tarif">
              <p>
                L'accès au service est facturé <strong>2,00 € TTC par planning généré</strong>{" "}
                via la plateforme sécurisée Stripe, ou proposé gratuitement selon la
                configuration en cours du service. Le paiement, lorsqu'il est demandé,
                s'effectue à l'unité et sans engagement.
              </p>
              <p className="text-slate-500 italic text-xs">
                Le prix affiché lors du paiement fait foi. En phase de test, les
                transactions peuvent être effectuées en mode "sandbox" (aucun débit réel).
              </p>
            </Section>

            <Section title="Article 3 — Livraison du service">
              <p>
                Lorsque le paiement est validé (ou lorsque l'accès est gratuit), le
                planning est généré <strong>immédiatement</strong>. Il est ensuite
                consultable, éditable et exportable depuis l'historique.
              </p>
            </Section>

            <Section title="Article 4 — Droit de rétractation">
              <p>
                Conformément à l'article L221-28 du Code de la consommation, le client
                reconnaît que le service est <strong>exécuté immédiatement</strong> avec
                son accord exprès dès le paiement. Le droit de rétractation ne s'applique
                donc pas une fois la génération lancée.
              </p>
            </Section>

            <Section title="Article 5 — Remboursement">
              <p>
                En cas de défaillance technique (échec de génération, planning
                inexploitable), le client peut demander un remboursement en écrivant à{" "}
                <em>[email de contact]</em>. Aucun autre motif ne donne droit à
                remboursement.
              </p>
            </Section>

            <Section title="Article 6 — Responsabilité">
              <p>
                Le planning généré est une <strong>aide à la décision</strong>. Il
                appartient exclusivement au client, professionnel du BTP, de vérifier la
                conformité du planning avec ses contraintes réelles (réglementaires,
                contractuelles, techniques, de sécurité). L'éditeur ne saurait être tenu
                responsable de dommages directs ou indirects résultant de l'utilisation du
                planning sans validation humaine.
              </p>
              <p>
                Compte tenu du caractère expérimental du service en phase de test,
                l'utilisateur reconnaît que la disponibilité, l'exactitude et la pérennité
                des données ne sont pas garanties.
              </p>
            </Section>

            <Section title="Article 7 — Modification, suspension">
              <p>
                L'éditeur se réserve le droit de modifier, suspendre ou interrompre le
                service à tout moment sans préavis. En cas d'interruption définitive, les
                utilisateurs seront invités à récupérer leurs données via l'export.
              </p>
            </Section>

            <Section title="Article 8 — Droit applicable">
              <p>
                Les présentes conditions sont soumises au droit français. Tout litige sera
                porté devant les tribunaux français compétents, après tentative de
                résolution amiable.
              </p>
            </Section>
          </article>
        </TabsContent>

        {/* ---------- Confidentialité / RGPD ---------- */}
        <TabsContent value="confidentialite" className="mt-6">
          <article className="prose max-w-none space-y-6" data-testid="content-confidentialite">
            <Section title="Responsable du traitement">
              <p>
                Le responsable du traitement est l'éditeur du site (particulier),
                joignable à l'adresse <em>[email de contact]</em>.
              </p>
            </Section>

            <Section title="Données collectées">
              <ul>
                <li>
                  <strong>Informations projet</strong> saisies dans le formulaire : nom du
                  projet, type, surface, dates, contraintes, description.
                </li>
                <li>
                  <strong>Documents téléversés</strong> (devis, mémoires techniques) —
                  traités en mémoire pour l'analyse, non stockés durablement sur nos
                  serveurs après génération.
                </li>
                <li>
                  <strong>Données de paiement</strong> gérées directement par Stripe ;
                  nous ne recevons que l'identifiant de session et le statut.
                </li>
              </ul>
            </Section>

            <Section title="Finalités">
              <p>
                Les données sont utilisées uniquement pour&nbsp;: (a) générer le planning
                demandé, (b) permettre la consultation ultérieure via l'historique.
              </p>
            </Section>

            <Section title="Sous-traitants">
              <ul>
                <li>
                  <strong>Anthropic (Claude Sonnet)</strong> — traitement du contenu pour
                  la génération du planning. Serveurs hors UE, garanties contractuelles
                  conformes RGPD.
                </li>
                <li>
                  <strong>Stripe</strong> — traitement des paiements. Certifié PCI-DSS.
                </li>
                <li>
                  <strong>MongoDB / Emergent</strong> — hébergement des plannings générés,
                  chiffrés au repos.
                </li>
              </ul>
            </Section>

            <Section title="Durée de conservation">
              <p>
                Les plannings sont conservés tant que l'utilisateur souhaite y accéder,
                supprimables à tout moment depuis l'historique.
              </p>
            </Section>

            <Section title="Vos droits">
              <p>
                Conformément au RGPD, vous disposez d'un droit d'accès, de rectification,
                d'effacement et d'opposition. Pour exercer ces droits, écrivez à{" "}
                <em>[email de contact]</em>. Vous pouvez également saisir la CNIL (
                <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">cnil.fr</a>).
              </p>
            </Section>

            <Section title="Cookies">
              <p>
                Le site utilise uniquement des cookies techniques strictement nécessaires
                (session, préférences). Aucun cookie publicitaire ou de mesure d'audience
                tiers n'est déposé.
              </p>
            </Section>
          </article>
        </TabsContent>
      </Tabs>

      <div className="mt-10 text-center text-xs text-slate-500 font-mono-tech">
        Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}
      </div>

      <div className="mt-4 text-center">
        <Link to="/" className="text-sm text-slate-500 underline underline-offset-4 hover:text-[#0F2137]">
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display font-bold text-xl text-[#0F2137] mb-2">{title}</h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}
